import { findDuplicates } from '@shared/helpers/array.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import {
  ValidationResult,
  GenericValidationError,
  ValidatedValue,
} from '@shared/validation/validation.models';
import { isValidActivity } from './activity.validation';
import { isValidStoreys } from './storey.validation';
import { getAllStoreys } from '../storey.helpers';
import { calculatedAreaActivityTypes } from '@shared/models/design/activities/activity-type.model';

export const DesignValidationErrors = {
  INVALID_LAT_LNG: 'Latitude/Longitude out of range',
  ACTIVITIES_NOT_ARRAY: 'Activities must be an array',
  DUPLICATE_ACTIVITY_IDS: 'Duplicate activity ids',
  MISSING_CALCULATED_AREA_ACTIVITY: 'Missing calculated area activity',
  MISSING_CALCULATED_AREA_ACTIVITY_REF:
    'Storey is missing calculated area activity reference',
  BUILDING_DESIGN_NOT_DEFINED: 'Building design is not defined',
  BOTTOM_STOREY_MULTIPLE_SHAPES: "Bottom storey can't have multiple shapes",
} as const;

/**
 * Validate if the project geometry is valid. DO NOT USE THIS GLOBALLY until the project geometry is fully migrated to the new format.
 * Will check activities and storeys are valid too.
 * @param design BuildingDesign to validate
 * @returns ValidationResult
 */
export const isValidBuildingDesign = (
  design: BuildingDesign,
): ValidationResult => {
  // Design must be defined
  if (!guards.is.defined(design)) {
    return new GenericValidationError(
      DesignValidationErrors.BUILDING_DESIGN_NOT_DEFINED,
    );
  }
  const { location, activities } = design;

  // Location must be a valid latitude and longitude
  if (
    !guards.is.numberBetween(location.lat, -90, 90) ||
    !guards.is.numberBetween(location.lng, -180, 180)
  ) {
    return new GenericValidationError(DesignValidationErrors.INVALID_LAT_LNG);
  }

  // Activities must be an array
  if (!guards.is.array(activities)) {
    return new GenericValidationError(
      DesignValidationErrors.ACTIVITIES_NOT_ARRAY,
    );
  }
  const activityIdDuplicates = findDuplicates(activities.map((a) => a.id));
  if (activityIdDuplicates.length) {
    return new GenericValidationError(
      DesignValidationErrors.DUPLICATE_ACTIVITY_IDS,
    );
  }
  for (const activity of activities) {
    const validActivity = isValidActivity(activity);
    if (validActivity !== true) return validActivity;
  }

  const validCalculatedAreaActivities = isValidCalculatedAreaActivities(design);
  if (validCalculatedAreaActivities !== true)
    return validCalculatedAreaActivities;

  // Storeys must be valid
  const validStoreys = isValidStoreys(design);
  if (validStoreys !== true) return validStoreys;

  return true;
};

const isValidCalculatedAreaActivities = (
  design: BuildingDesign,
): ValidationResult => {
  const storeys = getAllStoreys(design);
  if (!guards.is.array(storeys)) return true;

  for (const type of calculatedAreaActivityTypes) {
    const activity = design.activities.find((candidate) => {
      return candidate.type === type;
    });

    if (!activity) {
      return new GenericValidationError(
        DesignValidationErrors.MISSING_CALCULATED_AREA_ACTIVITY,
      );
    }

    const hasMissingStoreyRef = storeys.some((storey) => {
      return !storey.activities.some((ref) => {
        return ref.type === type && ref.activity_id === activity.id;
      });
    });

    if (hasMissingStoreyRef) {
      return new GenericValidationError(
        DesignValidationErrors.MISSING_CALCULATED_AREA_ACTIVITY_REF,
      );
    }
  }

  return true;
};

/**
 * Validate if the project design is valid. DO NOT USE THIS GLOBALLY until the project design is fully migrated to the new format.
 * @param design BuildingDesign to validate
 * @param onError Optional callback to handle validation errors
 * @returns BuildingDesign if valid
 */
export const validateBuildingDesign = (
  design: BuildingDesign,
): ValidatedValue<BuildingDesign> => {
  const valid = isValidBuildingDesign(design);
  return guards.is.error(valid) ? valid : design;
};
